/**
 * Equipo Page - Editorial Style
 * Team showcase with grayscale to color hover effect
 * Alternating black/white sections
 */

import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { PageWrapper } from '../components/EditorialShared';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fallback team data - Real Paliacate Studio team
const fallbackTeamMembers = [
  { 
    id: 1, 
    name: 'Luis Vega Canet', 
    role: 'CEO',
    initials: 'LV',
    bio: 'Fundador y director ejecutivo de Paliacate Studio.'
  },
  { 
    id: 2, 
    name: 'Edson Jair Fraga Chavez', 
    role: 'Coordinador Operativo',
    initials: 'EF',
    bio: 'Coordinación y gestión de operaciones del estudio.'
  },
  { 
    id: 3, 
    name: 'Danna Paola Garcia Najera', 
    role: 'Atención al Cliente + Comercial',
    initials: 'DG',
    bio: 'Relaciones con clientes y desarrollo comercial.'
  },
  { 
    id: 4, 
    name: 'Sheila Alejandra Uribe Pacheco', 
    role: 'Diseñadora Creativa Líder',
    initials: 'SU',
    bio: 'Liderazgo creativo y dirección de proyectos de diseño.'
  },
  { 
    id: 5, 
    name: 'Miriam Ortega Gutierrez', 
    role: 'Diseñadora Creativa',
    initials: 'MO',
    bio: 'Diseño de identidad visual y branding.'
  },
  { 
    id: 6, 
    name: 'Emiliano Heras Gonzalez', 
    role: 'Diseñador Creativo',
    initials: 'EH',
    bio: 'Diseño gráfico y contenido visual.'
  },
  { 
    id: 7, 
    name: 'Grecia Alizbeth Dominguez Flores', 
    role: 'Diseñadora Creativa',
    initials: 'GD',
    bio: 'Diseño gráfico y materiales de marca.'
  },
  { 
    id: 8, 
    name: 'Erik Alejandro Olascoaga Salgado', 
    role: 'Producción Audiovisual',
    initials: 'EO',
    bio: 'Fotografía y producción de video.'
  },
  { 
    id: 9, 
    name: 'Carlos Jose Garcia Valdez', 
    role: 'Producción Audiovisual',
    initials: 'CG',
    bio: 'Fotografía, video y edición.'
  }
];

// Get initials from name
const getInitials = (name) => {
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Team member card with grayscale to color hover effect
const TeamMemberCard = ({ member, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [imageError, setImageError] = useState(false);
  
  const hasPhoto = member.photo_url && !imageError;
  const initials = member.initials || getInitials(member.name);

  return (
    <motion.div
      ref={ref}
      className="group text-center"
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.08 }}
    >
      {/* Photo container with grayscale effect */}
      <div className="aspect-square overflow-hidden mb-6 rounded-full mx-auto w-36 md:w-44 relative">
        {hasPhoto ? (
          <img
            src={member.photo_url}
            alt={member.name}
            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-110"
            onError={() => setImageError(true)}
          />
        ) : (
          /* Placeholder with initials */
          <div className="w-full h-full bg-black flex items-center justify-center group-hover:bg-neutral-800 transition-colors duration-300">
            <span className="text-white text-3xl md:text-4xl font-light">
              {initials}
            </span>
          </div>
        )}
      </div>
      
      <h3 className="text-black text-lg md:text-xl font-medium mb-2 group-hover:opacity-70 transition-opacity">
        {member.name}
      </h3>
      <p className="text-black/50 text-sm uppercase tracking-wider">
        {member.role}
      </p>
    </motion.div>
  );
};

// Main component
const EditorialEquipoPage = () => {
  const [teamMembers, setTeamMembers] = useState(fallbackTeamMembers);
  const [loading, setLoading] = useState(true);
  
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  
  const teamRef = useRef(null);
  const isTeamInView = useInView(teamRef, { once: true, margin: "-10%" });

  // Try to load team from API
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await fetch(`${API_URL}/api/workers/public/team`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setTeamMembers(data);
          }
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, []);

  return (
    <PageWrapper showLogoFooter={true}>
      {/* Hero - BLACK background */}
      <section className="min-h-[60vh] flex items-end pb-20 pt-32 bg-black" ref={heroRef}>
        <div className="max-w-5xl mx-auto px-6">
          <motion.span
            className="text-white/40 text-sm tracking-widest uppercase mb-6 block"
            initial={{ opacity: 0, y: 20 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
          >
            Nuestro Equipo
          </motion.span>
          
          <motion.h1
            className="text-white text-5xl md:text-7xl lg:text-8xl font-extralight leading-[0.95] mb-8"
            initial={{ opacity: 0, y: 60 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.5 }}
          >
            Las personas detrás
            <br />
            de la magia
          </motion.h1>

          <motion.p
            className="text-white/50 text-xl md:text-2xl font-light max-w-2xl"
            initial={{ opacity: 0, y: 40 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.8 }}
          >
            Somos un equipo multidisciplinario apasionado por crear marcas 
            que conectan, inspiran y generan resultados.
          </motion.p>
        </div>
      </section>

      {/* Team Grid - WHITE background */}
      <section className="py-24 md:py-32 bg-white" ref={teamRef}>
        <div className="max-w-6xl mx-auto px-6">
          <motion.p
            className="text-black/40 text-sm uppercase tracking-[0.2em] mb-16 text-center"
            initial={{ opacity: 0 }}
            animate={isTeamInView ? { opacity: 1 } : {}}
          >
            Conoce al equipo
          </motion.p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-12 md:gap-16">
            {teamMembers.map((member, index) => (
              <TeamMemberCard 
                key={member.id || member.worker_id || index} 
                member={member} 
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Join section - BLACK background */}
      <section className="py-32 md:py-48 bg-black">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.h2
            className="text-white text-4xl md:text-6xl lg:text-7xl font-extralight mb-8"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            ¿Quieres unirte
            <br />
            al equipo?
          </motion.h2>

          <motion.p
            className="text-white/50 text-lg md:text-xl mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            Siempre estamos buscando talento apasionado.
          </motion.p>

          <motion.a
            href="https://wa.me/527774385617"
            target="_blank"
            rel="noopener noreferrer"
            className="px-10 py-5 bg-white text-black font-medium hover:bg-white/90 transition-colors inline-block"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            data-cursor="contactar"
          >
            Contáctanos
          </motion.a>
        </div>
      </section>
    </PageWrapper>
  );
};

export default EditorialEquipoPage;
